import React, { useEffect, useState } from "react";
import { isLoggedIn } from "api/coachAuth";
import TaskHeader from "components/task/header/TaskHeader";
import useGetTaskData from "hooks/useGetTaskData";
import TaskContent from "components/task/content/TaskContent";
import { Box, Stack } from "@mui/joy";
import { ProgramContentType } from "data/BlockComponent";
import * as _ from "lodash";
import useInterval from "hooks/useInterval";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { taskListAtom } from "recoil/taskListAtom";
import { ProgramType } from "data/programData";
import { userAtom } from "recoil/userAtom";
import useSetConceptData from "hooks/useSetConceptData";
import useGetCertainEventFromApp from "hooks/useGetCertainEventFromApp";
import { selectedAutomaticThoughtIdAtom } from "../../recoil/selectedAutomaticThoughtIdAtom";
import { selectedConceptNoteSectionAtom } from "../../recoil/selectedConceptNoteSectionAtom";
import { getTaskIdFromTaskKey } from "../../logic/logics";

export default function Activity({
  taskKey,
  noHeader,
}: {
  taskKey: string;
  noHeader?: boolean;
}) {
  const user = useRecoilValue(userAtom);
  const setSelectedAutomaticThoughtId = useSetRecoilState(
    selectedAutomaticThoughtIdAtom
  );
  const setSelectedConceptNoteSection = useSetRecoilState(
    selectedConceptNoteSectionAtom
  );
  const isCoach =
    isLoggedIn(user) && user?.role !== "patient" && !taskKey.includes("Tool");

  const taskListState = useRecoilValue(taskListAtom);
  const currentTaskState = taskListState.find(
    (element) => element.taskKey === taskKey
  );

  const [data, setData] = useState<ProgramType | undefined>();

  const [contentData, setContentData] = useState<
    ProgramContentType[] | undefined
  >();
  const [prevContentData, setPrevContentData] = useState<
    ProgramContentType[] | undefined
  >();

  const { data: fetchedData, refetch } = useGetTaskData({
    taskKey: `${taskKey}`,
  });

  // 개념화노트, 생각지도 데이터 세팅
  const { isConceptNoteFetching, isConceptFetching } = useSetConceptData({
    taskKey: taskKey,
  });

  useEffect(() => {
    if (isCoach) {
      const taskId = getTaskIdFromTaskKey(taskKey);
      const baseId = taskId.split("_")[0];

      // Map belief-related tasks to concept note sections
      if (baseId === "1-2-A") {
        setSelectedConceptNoteSection("concernAndGoal");
      } else if (baseId === "6-3-A") {
        setSelectedConceptNoteSection("intermediate");
      } else if (baseId === "6-6-A") {
        setSelectedConceptNoteSection("core");
      } else if (baseId === "6-8-A") {
        setSelectedConceptNoteSection("experience");
      } else {
        setSelectedConceptNoteSection(undefined);
      }

      const isForValue =
        baseId === "7-4-A" ||
        baseId === "7-6-A" ||
        baseId === "7-7-A" ||
        baseId === "7-8-A" ||
        baseId === "7-9-A";

      if (taskId.includes("_")) {
        const afterUnderscore = taskId.split("_")[1];
        const idStr = afterUnderscore.split("#")[0];
        const parsed = parseInt(idStr, 10);
        if (!Number.isNaN(parsed)) {
          setSelectedAutomaticThoughtId({ id: parsed, isForValue });
        }
      } else {
        setSelectedAutomaticThoughtId(undefined);
      }
    }
  }, [isCoach, taskKey]);

  useEffect(() => {
    return () => {
      setSelectedAutomaticThoughtId(undefined);
      setSelectedConceptNoteSection(undefined);
    };
  }, []);

  useInterval(
    () => {
      setPrevContentData(contentData);
      //변화 없으면 다시 가져와
      if (
        contentData &&
        prevContentData &&
        _.isEqual(contentData, prevContentData)
      ) {
        refetch();
      }
    },
    10000,
    []
  );

  const refetchActivityEvent = useGetCertainEventFromApp({
    eventName: "refetch_activity",
  });

  useEffect(() => {
    if (refetchActivityEvent) {
      refetch();
    }
  }, [refetchActivityEvent]);

  useEffect(() => {
    if (fetchedData) {
      setData(fetchedData);
    }
  }, [fetchedData]);

  useEffect(() => {
    setContentData(data?.content);
  }, [data]);

  const [moveToIndex, setMoveToIndex] = useState<number | undefined>(undefined);

  return (
    <>
      <Box
        sx={{
          flex: "none",
          zIndex: 100,
          // px: { xs: "16px", sm: "24px" },
        }}
      >
        {!noHeader && (
          <TaskHeader
            taskKey={taskKey}
            contentData={contentData}
            setContentData={setContentData}
            setData={setData}
            setMoveToIndex={setMoveToIndex}
            isDone={currentTaskState?.isDone}
            isOpen={currentTaskState?.isOpen}
            isInitialized={!data}
            lastDoneAt={currentTaskState?.lastDoneAt}
          />
        )}
      </Box>

      <Stack sx={{ flex: 1, overflow: "hidden" }}>
        <TaskContent
          key={taskKey}
          taskKey={taskKey}
          data={contentData}
          setData={setContentData}
          moveToIndex={moveToIndex}
          setMoveToIndex={setMoveToIndex}
          isDone={currentTaskState?.isDone}
          isOpen={currentTaskState?.isOpen}
          translationVersion={data?.translationVersion}
        />
      </Stack>
    </>
  );
}
